import { skipToken, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { usePublishedContext } from '@/components/views/published/PublishedContext';
import type { ItemType } from '@/lib/api/types/itemType';
import { itemTypesQueryOptions } from '@/lib/queries/common/useItemTypesQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { associateById } from '@/lib/utils/common/associateById';

export const useItemTypesQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = useQuery<ItemType[] | undefined>({
    queryKey: [...commonQueryKeys.itemTypes.all, 'published'],
    queryFn: skipToken,
    initialData: () => publishedContext?.itemTypes,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};

export const useItemTypesByIdQueryWithPublished = (
  options: Partial<UseQueryOptions<ItemType[]>> = {},
) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;

  const itemTypesQuery = useQuery({
    ...itemTypesQueryOptions(),
    ...options,
    select: associateById,
    enabled: !isPublished && options.enabled !== false,
  });

  const publishedQuery = useQuery<ItemType[] | undefined, Error, Record<number, ItemType>>({
    queryKey: [...commonQueryKeys.itemTypes.all, 'published'],
    queryFn: skipToken,
    initialData: () => publishedContext?.itemTypes,
    select: (data) => (data ? associateById(data) : {}),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return isPublished ? publishedQuery : itemTypesQuery;
};
